import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  fetchOrders,
  updateOrderStatus,
  completeOrder,
  deleteOrder
} from "../services/api";
import Card from "../components/Card";
import EditOrderModal from "../components/EditOrderModal";
import {
  formatDate,
  formatCurrency,
  formatDateInEastern,
  formatDateInEasternDT,
  formatDateCompact,
  formatPatientDob as formatPatientDobUtil
} from "../utils/formatters";
import {
  FaEye,
  FaEdit,
  FaCheck,
  FaSpinner,
  FaChevronDown,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCheckCircle,
  FaTimesCircle,
  FaReceipt,
  FaTrash,
  FaCopy,
  FaCalendarAlt,
  FaTruck,
  FaBoxOpen,
  FaTimes
} from "react-icons/fa";

// Styled components
const PageWrapper = styled.div`
  padding: 24px;
  background-color: var(--bg-primary);
  min-height: 100vh;
  width: 100%;
`;

const OrdersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const OrdersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const OrdersTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CreateOrderBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--primary-dark);
    text-decoration: none;
    color: white;
  }

  svg {
    margin-right: 0.5rem;
  }
`;

const OrdersTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  th,
  td {
    padding: 1rem 1.25rem;
    text-align: left;
    border-bottom: 1px solid var(--border-color);
  }

  th {
    background-color: var(--card-header-bg);
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    /* Column width constraints */
    &:nth-child(1) { /* ID */
      min-width: 80px;
      width: 80px;
    }
    
    &:nth-child(3) { /* Name */
      min-width: 120px;
      width: 120px;
    }
    
    &:nth-child(4) { /* DOB */
      min-width: 100px;
      width: 100px;
    }
  }

  tbody tr {
    background-color: var(--card-bg);
    transition: all 0.2s ease;
    
    &:hover {
      background-color: var(--hover-color);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }
  }

  tbody td {
    vertical-align: middle;
    
    /* ID column */
    &:nth-child(1) {
      font-weight: 500;
      color: var(--text-primary);
      min-width: 80px;
      width: 80px;
    }
    
    /* Name column */
    &:nth-child(3) {
      font-weight: 500;
      color: var(--text-primary);
      min-width: 120px;
      width: 120px;
    }
    
    /* DOB column */
    &:nth-child(4) {
      color: var(--text-secondary);
      font-size: 0.875rem;
      min-width: 100px;
      width: 100px;
    }
    
    /* Total Fee and Status columns */
    &:nth-child(9), /* Total Fee */
    &:nth-child(10) /* Status */ {
      font-weight: 500;
      color: var(--text-primary);
    }
    
    /* Order Type column */
    &:nth-child(6) {
      color: var(--text-secondary);
      font-size: 0.875rem;
    }
  }
`;

const PageTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--gray-900);
  margin: 0;
`;

const OrderItemsTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--border-color);

  th,
  td {
    padding: 0.875rem 1rem;
    text-align: left;
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border-color);
  }

  th {
    background-color: var(--card-header-bg);
    font-weight: 600;
    color: var(--text-primary);
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    
    &:first-child:before { content: "💊 "; }
    &:nth-child(2):before { content: "🧪 "; }
    &:nth-child(3):before { content: "📝 "; }
    &:nth-child(4):before { content: "💉 "; }
    &:nth-child(5):before { content: "💲 "; }
    &:nth-child(6):before { content: "🚚 "; }
    
    /* Medicine column header width */
    &:nth-child(1) {
      width: 400px;
      min-width: 400px;
    }

    /* Dosage column header width */
    &:nth-child(4) {
      width: 400px;
      min-width: 400px;
    }
  }

  tbody tr {
    background-color: var(--card-bg);
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: var(--hover-color);
    }
    
    &:last-child td {
      border-bottom: none;
    }
  }

  td {
    vertical-align: top;
    line-height: 1.5;
    
    /* Medicine column styling with text wrapping */
    &:nth-child(1) {
      width: 400px;
      min-width: 400px;
      max-width: 400px;
      word-wrap: break-word;
      white-space: normal;
      overflow-wrap: break-word;
    }

    /* Dosage column styling with text wrapping */
    &:nth-child(4) {
      width: 400px;
      min-width: 400px;
      max-width: 400px;
      word-wrap: break-word;
      white-space: normal;
      overflow-wrap: break-word;
    }
  }
`;

const ExpandButton = styled.button`
  background: linear-gradient(135deg, var(--primary-color), var(--primary-dark));
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  svg {
    width: 12px;
    height: 12px;
    transition: transform 0.3s ease;
    transform: rotate(${(props) => (props.expanded ? "180deg" : "0deg")});
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => 
    props.variant === 'info' ? 'var(--info-light)' :
    props.variant === 'warning' ? 'var(--warning-light)' :
    props.variant === 'success' ? 'var(--success-light)' :
    'var(--bg-secondary)'
  };
  color: ${props => 
    props.variant === 'info' ? 'var(--info)' :
    props.variant === 'warning' ? 'var(--warning)' :
    props.variant === 'success' ? 'var(--success)' :
    'var(--text-secondary)'
  };
  border: 1px solid ${props => 
    props.variant === 'info' ? 'var(--info)' :
    props.variant === 'warning' ? 'var(--warning)' :
    props.variant === 'success' ? 'var(--success)' :
    'var(--border-color)'
  };
`;

const PatientNameLink = styled(Link)`
  font-weight: 600;
  color: var(--primary-color);
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: var(--primary-dark);
    text-decoration: underline;
  }
`;

const DateContainer = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-primary);
  background: var(--card-bg);
  padding: 0.4rem 0.7rem;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  display: inline-block;
  min-width: 85px;
  text-align: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    border-color: var(--primary-color);
  }
`;

const AddressContainer = styled.div`
  font-size: 0.85rem;
  line-height: 1.4;
  padding: 0.75rem;
  border-radius: 8px;
  background: linear-gradient(145deg, var(--card-bg), var(--hover-color));
  border: 1px solid var(--border-color);
  transition: all 0.2s ease;
  min-width: 180px;
  max-width: 250px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .street {
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 0.25rem;
  }
  
  .city-state {
    color: var(--text-secondary);
    font-size: 0.8rem;
  }
`;

const OrderItemsContainer = styled.div`
  background-color: var(--card-bg);
  padding: 1.5rem;
  margin: 0.75rem 0;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;

const NoOrdersMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
  font-size: 1rem;
`;

const OrderStatus = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  text-align: center;
  min-width: fit-content;
  background-color: ${(props) => {
    if (props.status === "to_be_posted") return "var(--info-light)";
    if (props.status === "in_progress") return "var(--warning-light)";
    if (props.status === "failed") return "var(--error-bg)";
    if (props.status === "completed") return "var(--success-light)";
    if (props.status === "approved") return "var(--success-light)";
    return "var(--warning-light)";
  }};
  color: ${(props) => {
    if (props.status === "to_be_posted") return "var(--info)";
    if (props.status === "in_progress") return "var(--warning)";
    if (props.status === "failed") return "var(--error)";
    if (props.status === "completed") return "var(--success)";
    if (props.status === "approved") return "var(--success)";
    return "var(--warning)";
  }};
`;

const ActionLinks = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover:not(:disabled) {
    background-color: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: var(--bg-secondary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ReceiptButton = styled(IconButton)`
  color: var(--primary-color);
  border-color: var(--primary-color);

  &:hover:not(:disabled) {
    background-color: var(--primary-color);
    color: white;
    transform: translateY(-1px);
  }
`;

// Add new styled component for the copy button
const CopyButton = styled(IconButton)`
  color: var(--text-secondary);

  &:hover {
    color: var(--primary-color);
  }
`;

// Add new styled component for receipt actions container
const ReceiptActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const DeleteButton = styled(IconButton)`
  color: var(--error);

  &:hover {
    background-color: var(--error-bg);
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  font-size: 0.875rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessButton = styled(Button)`
  background-color: var(--success-light);
  color: var(--success);

  &:hover:not(:disabled) {
    background-color: var(--success-light-hover);
  }
`;
const PageDescription = styled.p`
  color: var(--text-secondary, #aaa);
  font-size: 1rem;
  max-width: 800px;
`;
const DangerButton = styled(Button)`
  background-color: var(--error-bg);
  color: var(--error);

  &:hover:not(:disabled) {
    background-color: var(--error-bg-hover);
  }
`;

const ApproveButton = styled(Button)`
  background-color: #d1fae5;
  color: #059669;

  &:hover:not(:disabled) {
    background-color: #a7f3d0;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--card-bg);
  border-radius: 8px;
  padding: 1.5rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  margin-bottom: 1rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const ModalBody = styled.div`
  margin-bottom: 1.5rem;
  color: var(--text-primary);
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const CancelButton = styled(Button)`
  background-color: var(--bg-secondary-button);
  color: var(--text-primary);

  &:hover:not(:disabled) {
    background-color: var(--bg-secondary-button-hover);
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid var(--border-color);
  background-color: ${(props) =>
    props.$active ? "var(--primary-color)" : "var(--bg-secondary)"};
  color: ${(props) => (props.$active ? "white" : "var(--text-primary)")};

  &:hover {
    background-color: ${(props) =>
      props.$active ? "var(--primary-dark)" : "var(--hover-color)"};
  }
`;

const SearchContainer = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
  width: 100%;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex-grow: 1;
  max-width: 480px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  font-size: 1rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.1);
  }

  &::placeholder {
    color: var(--text-tertiary);
  }
`;

const SearchButton = styled(Button)`
  background-color: var(--primary-color);
  color: white;
  margin-left: 0.5rem;

  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

const SortLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const SortSelect = styled.select`
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const SortIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0.25rem;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    color: var(--text-primary);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SortableHeader = styled.th`
  cursor: pointer;
  user-select: none;
  position: relative;
  text-align: left;

  &:hover {
    background-color: var(--hover-color);
  }

  .sort-icon {
    margin-left: 0.5rem;
    display: inline-flex;
    align-items: center;
    vertical-align: middle;
    float: none;
  }
`;

const PageHeader = styled.div``;

const LoadingMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem;
  color: var(--text-secondary);
  font-size: 1rem;
  
  svg {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div``;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1rem;
  border-top: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const PageInfo = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageButton = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid var(--border-color);
  background-color: ${(props) =>
    props.$active ? "var(--primary-color)" : "var(--bg-secondary)"};
  color: ${(props) => (props.$active ? "white" : "var(--text-primary)")};
  min-width: 40px;

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$active ? "var(--primary-dark)" : "var(--hover-color)"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PerPageContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PerPageLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const PerPageSelect = styled.select`
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const PageInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 0.5rem;
`;

const PageInputLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const PageInput = styled.input`
  width: 60px;
  padding: 0.5rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
  text-align: center;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const GoButton = styled(PageButton)`
  padding: 0.5rem 0.75rem;
  min-width: unset;
`;

const TopControls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PharmacyResponseContainer = styled.div`
  margin-top: 1rem;
`;

const PharmacyResponseHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.5rem;
`;

const ResponseStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: ${(props) => (props.success ? "var(--success)" : "var(--error)")};
`;

const MedicationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MedicationItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => (props.success ? "var(--success)" : "var(--error)")};
`;

const ErrorList = styled.ul`
  list-style: disc;
  padding-left: 1.5rem;
  color: var(--error);
  margin-top: 1rem;
`;

// Add new styled component for copy notification
const CopyNotification = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  background-color: #4b5563;
  color: white;
  padding: 12px 16px;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DateFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const DateFilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;

  svg {
    color: var(--primary-color);
  }
`;

const DateInput = styled.input`
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 0.875rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.1);
  }
`;

const ClearFiltersButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: var(--error-bg);
    color: var(--error);
    border-color: var(--error);
  }

  svg {
    width: 12px;
    height: 12px;
  }
`;

const PharmacyDeliveryFilterLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;

  svg {
    color: var(--primary-color);
  }
`;

const PharmacyDeliveryStatus = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.2rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  background-color: ${(props) => {
    if (props.status === "shipment_created") return "#dbeafe";
    if (props.status === "shipment_delivered") return "#ecfdf5";
    return "var(--bg-secondary)";
  }};
  color: ${(props) => {
    if (props.status === "shipment_created") return "#2563eb";
    if (props.status === "shipment_delivered") return "#059669";
    return "var(--text-secondary)";
  }};
  border: 1px solid ${(props) => {
    if (props.status === "shipment_created") return "#93c5fd";
    if (props.status === "shipment_delivered") return "#6ee7b7";
    return "var(--border-color)";
  }};

  svg {
    width: 10px;
    height: 10px;
  }
`;

const STORAGE_KEY_PREFIX = "orders_page_";
const STORAGE_KEYS = {
  PAGE_SIZE: `${STORAGE_KEY_PREFIX}page_size`,
  CURRENT_PAGE: `${STORAGE_KEY_PREFIX}current_page`,
  STATUS_FILTER: `${STORAGE_KEY_PREFIX}status_filter`,
  SORT_FIELD: `${STORAGE_KEY_PREFIX}sort_field`,
  SORT_ORDER: `${STORAGE_KEY_PREFIX}sort_order`,
  SEARCH_TERM: `${STORAGE_KEY_PREFIX}search_term`,
  EXPANDED_ORDERS: `${STORAGE_KEY_PREFIX}expanded_orders`,
  START_DATE: `${STORAGE_KEY_PREFIX}start_date`,
  END_DATE: `${STORAGE_KEY_PREFIX}end_date`,
  PHARMACY_DELIVERY_STATUS: `${STORAGE_KEY_PREFIX}pharmacy_delivery_status`
};

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const OrdersPage = () => {
  const getStoredValue = (key, defaultValue) => {
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
    } catch (e) {
      console.error(`Error reading from localStorage for key ${key}:`, e);
      return defaultValue;
    }
  };

  const storeValue = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`Error storing in localStorage for key ${key}:`, e);
    }
  };

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [orderToApprove, setOrderToApprove] = useState(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pageInputValue, setPageInputValue] = useState("");
  const [expandedOrders, setExpandedOrders] = useState(
    getStoredValue(STORAGE_KEYS.EXPANDED_ORDERS, {})
  );
  const [statusFilter, setStatusFilter] = useState(
    getStoredValue(STORAGE_KEYS.STATUS_FILTER, "to_be_posted")
  );
  const [searchTerm, setSearchTerm] = useState(
    getStoredValue(STORAGE_KEYS.SEARCH_TERM, "")
  );
  const [startDate, setStartDate] = useState(
    getStoredValue(STORAGE_KEYS.START_DATE, "")
  );
  const [endDate, setEndDate] = useState(
    getStoredValue(STORAGE_KEYS.END_DATE, "")
  );
  const [pharmacyDeliveryFilter, setPharmacyDeliveryFilter] = useState(
    getStoredValue(STORAGE_KEYS.PHARMACY_DELIVERY_STATUS, "all")
  );
  const [sortField, setSortField] = useState(
    getStoredValue(STORAGE_KEYS.SORT_FIELD, "order_id")
  );
  const [sortOrder, setSortOrder] = useState(
    getStoredValue(STORAGE_KEYS.SORT_ORDER, "desc")
  );
  const [itemsPerPage, setItemsPerPage] = useState(
    getStoredValue(STORAGE_KEYS.PAGE_SIZE, 25)
  );
  // The filter effects below reset the page to 1 on mount anyway; starting at 1
  // avoids fetching the stored page first and page 1 right after.
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    total_pages: 0,
    current_page: currentPage,
    page_size: itemsPerPage
  });
  const [isSearching, setIsSearching] = useState(false);
  const [user, setUser] = useState(null);
  const [pharmacyResponse, setPharmacyResponse] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  // Bumped to refetch the list; reqSeq lets only the latest request update state
  const [refreshTick, setRefreshTick] = useState(0);
  const reqSeq = useRef(0);
  // New state for copy URL notification
  const [copyNotification, setCopyNotification] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState(null);
  
  // Error handling state
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const navigate = useNavigate();

  useEffect(() => {
    setPageInputValue(currentPage.toString());
  }, [currentPage]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.EXPANDED_ORDERS, expandedOrders);
  }, [expandedOrders]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.STATUS_FILTER, statusFilter);
    setCurrentPage(1);
  }, [statusFilter]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.SEARCH_TERM, searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.START_DATE, startDate);
    if (startDate) setCurrentPage(1);
  }, [startDate]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.END_DATE, endDate);
    if (endDate) setCurrentPage(1);
  }, [endDate]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.PHARMACY_DELIVERY_STATUS, pharmacyDeliveryFilter);
    setCurrentPage(1);
  }, [pharmacyDeliveryFilter]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.SORT_FIELD, sortField);
    setCurrentPage(1);
  }, [sortField]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.SORT_ORDER, sortOrder);
    setCurrentPage(1);
  }, [sortOrder]);

  useEffect(() => {
    storeValue(STORAGE_KEYS.PAGE_SIZE, itemsPerPage);
    setCurrentPage(1);
  }, [itemsPerPage]);

  useEffect(() => {
    if (pagination.current_page) {
      storeValue(STORAGE_KEYS.CURRENT_PAGE, pagination.current_page);
      setPageInputValue(pagination.current_page.toString());
    }
  }, [pagination.current_page]);

  const fetchParams = useMemo(
    () => ({
      page: currentPage,
      statusFilter,
      searchTerm: debouncedSearchTerm,
      sortField,
      sortOrder,
      itemsPerPage,
      startDate,
      endDate,
      pharmacyDeliveryFilter
    }),
    [
      currentPage,
      statusFilter,
      debouncedSearchTerm,
      sortField,
      sortOrder,
      itemsPerPage,
      startDate,
      endDate,
      pharmacyDeliveryFilter
    ]
  );

  useEffect(() => {
    const userString = localStorage.getItem("user");
    if (userString) {
      const userObject = JSON.parse(userString);
      setUser(userObject);
    }
  }, []);

  useEffect(() => {
    getOrders();
  }, [fetchParams, refreshTick]);

  const getOrders = async () => {
    const seq = ++reqSeq.current;

    setLoading(true);
    try {
      const sortString = `${fetchParams.sortOrder === "desc" ? "-" : ""}${
        fetchParams.sortField
      }`;

      const response = await fetchOrders(
        fetchParams.page,
        fetchParams.statusFilter,
        fetchParams.searchTerm,
        sortString,
        fetchParams.itemsPerPage,
        {
          startDate: fetchParams.startDate,
          endDate: fetchParams.endDate,
          pharmacyDeliveryStatus: fetchParams.pharmacyDeliveryFilter
        }
      );

      // A newer request was started while this one was in flight
      if (seq !== reqSeq.current) return;

      setOrders(response.results || []);

      setPagination({
        count: response.count || 0,
        next: response.next,
        previous: response.previous,
        total_pages: response.total_pages || 1,
        current_page: response.current_page || fetchParams.page,
        page_size: response.page_size || fetchParams.itemsPerPage
      });

      setError(null);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      console.error("Error fetching orders:", err);
      setError("Failed to load orders");
      setOrders([]);
    } finally {
      if (seq === reqSeq.current) {
        setLoading(false);
        setIsSearching(false);
      }
    }
  };

  const refreshData = () => {
    setRefreshTick((t) => t + 1);
  };

  const forceRefresh = async () => {
    setLoading(true);
    setRefreshTick((t) => t + 1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = (e) => {
    if (e.type === "click" || (e.type === "keydown" && e.key === "Enter")) {
      setIsSearching(true);
      setCurrentPage(1);
      // Nothing else will trigger a fetch when the page is already 1 and the
      // debounced term is current, which left the button stuck on "Searching..."
      if (currentPage === 1 && searchTerm === debouncedSearchTerm) {
        refreshData();
      }
    }
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handleSortFieldChange = (e) => {
    setSortField(e.target.value);
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleHeaderClick = (field) => {
    if (sortField === field) {
      toggleSortOrder();
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return <FaSort />;
    }
    return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
  };

  const handleApproveOrder = async (orderId) => {
    setUpdateLoading((prev) => ({ ...prev, [orderId]: "approve" }));
    try {
      await updateOrderStatus(orderId, "approved");
      
      // Update local state immediately for better UX
      const updatedOrders = orders.map(order => 
        order.order_id === orderId 
          ? { ...order, status: "approved" }
          : order
      );
      setOrders(updatedOrders);
      
      // Force refresh to ensure we have the latest data
      forceRefresh();
      
      // Show success message
      setSuccessMessage(`Order #${orderId} has been approved successfully.`);
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (err) {
      console.error("Error approving order:", err);
    } finally {
      setUpdateLoading((prev) => ({ ...prev, [orderId]: null }));
    }
  };

  const handleRejectOrder = async (orderId) => {
    setUpdateLoading((prev) => ({ ...prev, [orderId]: "reject" }));
    try {
      await updateOrderStatus(orderId, "failed");
      const updatedOrders = [...orders];
      const orderIndex = updatedOrders.findIndex(
        (order) => order.order_id === orderId
      );

      if (orderIndex !== -1) {
        updatedOrders[orderIndex] = {
          ...updatedOrders[orderIndex],
          status: "failed"
        };
        setOrders(updatedOrders);
      }
      
      // Force refresh to ensure we have the latest data
      forceRefresh();
      
      // Show success message
      setSuccessMessage(`Order #${orderId} has been rejected successfully.`);
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (err) {
      console.error("Error rejecting order:", err);
    } finally {
      setUpdateLoading((prev) => ({ ...prev, [orderId]: null }));
    }
  };

  const openApprovalModal = (order) => {
    setOrderToApprove(order);
    setShowApprovalModal(true);
  };

  const closeApprovalModal = () => {
    setShowApprovalModal(false);
    setOrderToApprove(null);
  };

  const openDeleteModal = (order) => {
    setOrderToDelete(order);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setOrderToDelete(null);
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;

    setDeleteLoading(true);
    try {
      await deleteOrder(orderToDelete.order_id);

      let pageToLoad = pagination.current_page;
      if (orders.length === 1 && pagination.current_page > 1) {
        setCurrentPage(pagination.current_page - 1);
      }

      closeDeleteModal();

      setSuccessMessage(
        `Order #${orderToDelete.order_id} has been deleted successfully.`
      );

      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);

      forceRefresh();
    } catch (error) {
      setError(`Failed to delete order: ${error.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  const [successMessage, setSuccessMessage] = useState("");

  const closeResponseModal = () => {
    setShowResponseModal(false);
    setPharmacyResponse(null);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
  };

  const handleCompleteOrder = async () => {
    if (!orderToApprove) return;

    setApprovalLoading(true);
    try {
      const response = await completeOrder(orderToApprove.order_id);

      if (response.pharmacy_response) {
        setPharmacyResponse(response.pharmacy_response);
        setShowApprovalModal(false);
        setShowResponseModal(true);
      }

      // Update local state immediately for better UX
      const updatedOrders = orders.map(order => 
        order.order_id === orderToApprove.order_id 
          ? { ...order, status: "completed" }
          : order
      );
      setOrders(updatedOrders);

      // Force refresh to ensure we have the latest data
      forceRefresh();

      if (!response.pharmacy_response) {
        setShowApprovalModal(false);
        
        // Show success message
        setSuccessMessage(`Order #${orderToApprove.order_id} has been completed successfully.`);
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      }
    } catch (err) {
      console.error("Error completing order:", err);
      
      // Handle 409 conflict error specifically
      if (err.status === 409 && err.data) {
        const { message, current_status, order_id } = err.data;
        setErrorMessage(
          `${message}. Order ${order_id} is currently in "${current_status}" status.`
        );
        setShowApprovalModal(false);
        setShowErrorModal(true);
        forceRefresh(); // Refresh to show updated status
      } else if (err.response && err.response.data) {
        if (err.response.data.pharmacy_response) {
          setPharmacyResponse(err.response.data.pharmacy_response);
          setShowApprovalModal(false);
          setShowResponseModal(true);
        } else {
          // Show generic error
          setErrorMessage(err.message || "Failed to complete order. Please try again.");
          setShowApprovalModal(false);
          setShowErrorModal(true);
        }
      } else {
        // Show generic error
        setErrorMessage(err.message || "Failed to complete order. Please try again.");
        setShowApprovalModal(false);
        setShowErrorModal(true);
      }
    } finally {
      setApprovalLoading(false);
      setOrderToApprove(null);
    }
  };

  const getStatusText = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusDisplayText = (status) => {
    switch (status) {
      case "to_be_posted":
        return "To Be Posted";
      case "in_progress":
        return "In Progress";
      case "approved":
        return "Approved";
      case "failed":
        return "Failed";
      case "completed":
        return "Completed";
      default:
        return getStatusText(status);
    }
  };

  const getPharmacyDeliveryStatusText = (status) => {
    switch (status) {
      case "shipment_created":
        return "Shipment Created";
      case "shipment_delivered":
        return "Shipment Delivered";
      default:
        return status;
    }
  };

  const clearDateFilters = () => {
    setStartDate("");
    setEndDate("");
    setPharmacyDeliveryFilter("all");
    setCurrentPage(1);
  };

  const hasActiveFilters = startDate || endDate || pharmacyDeliveryFilter !== "all";

  const handleOpenEditModal = (order) => {
    navigate(`/orders/${order.order_id}/edit`);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedOrder(null);
  };

  const handleOrderUpdated = () => {
    forceRefresh();
    handleCloseEditModal();
  };

  const handleViewOrderDetails = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  // Function to navigate to order receipt
  const handleViewReceipt = (orderId) => {
    navigate(`/orders/${orderId}/receipt`);
  };

  // New function to handle copying the receipt URL with the correct format
  const handleCopyReceiptUrl = (orderId, e) => {
    e.stopPropagation(); // Prevent triggering other click events

    
    // Construct the URL with the format orders.com/receipt/{orderId}
    const receiptUrl =
      import.meta.env.PROD_ENV === "development"
        ? "http://localhost:3001" + `receipt/${orderId}`
        : "https://app.lfrxportal.com/" + `receipt/${orderId}`;

    // Copy to clipboard
    navigator.clipboard
      .writeText(receiptUrl)
      .then(() => {
        // Show notification
        setCopiedOrderId(orderId);
        setCopyNotification(true);

        // Hide notification after 2 seconds
        setTimeout(() => {
          setCopyNotification(false);
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy URL: ", err);
      });
  };

  const toggleOrderItems = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    setCurrentPage(page);
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setPageInputValue(value);
  };

  const handleGoToPage = () => {
    const pageNum = parseInt(pageInputValue, 10);

    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= pagination.total_pages) {
      handlePageChange(pageNum);
    } else {
      setPageInputValue(pagination.current_page.toString());
    }
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === "Enter") {
      handleGoToPage();
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    setItemsPerPage(newPageSize);
  };

  const startIndex =
    pagination.count === 0
      ? 0
      : (pagination.current_page - 1) * pagination.page_size + 1;

  const endIndex = Math.min(
    startIndex + pagination.page_size - 1,
    pagination.count
  );



  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Orders</PageTitle>
        <PageDescription>Manage and track all orders</PageDescription>
      </PageHeader>

      {successMessage && (
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: "rgba(16, 185, 129, 0.1)",
            color: "#10b981",
            borderRadius: "4px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          <FaCheckCircle />
          {successMessage}
        </div>
      )}

      <SearchContainer>
        <SearchInputWrapper>
          <SearchIcon>
            <FaSearch />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search by order ID, patient name, email, phone, DOB, or medicine..."
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearch}
          />
        </SearchInputWrapper>
        <SearchButton onClick={handleSearch} disabled={isSearching}>
          {isSearching ? "Searching..." : "Search"}
        </SearchButton>
        {searchTerm && (
          <Button onClick={clearSearch} style={{ marginLeft: "0.5rem" }}>
            Clear
          </Button>
        )}
      </SearchContainer>

      <FilterContainer>
        <FilterButton
          $active={statusFilter === "all"}
          onClick={() => setStatusFilter("all")}
        >
          All Orders
        </FilterButton>
        <FilterButton
          $active={statusFilter === "to_be_posted"}
          onClick={() => setStatusFilter("to_be_posted")}
        >
          To Be Posted
        </FilterButton>
        <FilterButton
          $active={statusFilter === "in_progress"}
          onClick={() => setStatusFilter("in_progress")}
        >
          In Progress
        </FilterButton>
        <FilterButton
          $active={statusFilter === "completed"}
          onClick={() => setStatusFilter("completed")}
        >
          Completed
        </FilterButton>
        <FilterButton
          $active={statusFilter === "failed"}
          onClick={() => setStatusFilter("failed")}
        >
          Failed
        </FilterButton>
      </FilterContainer>

      <DateFilterContainer>
        <DateFilterLabel>
          <FaCalendarAlt />
          Date Range:
        </DateFilterLabel>
        <DateInput
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Start date"
        />
        <span style={{ color: "var(--text-secondary)", fontSize: "0.875rem" }}>to</span>
        <DateInput
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          placeholder="End date"
        />
        <PharmacyDeliveryFilterLabel>
          <FaTruck />
          Pharmacy Delivery:
        </PharmacyDeliveryFilterLabel>
        <FilterButton
          $active={pharmacyDeliveryFilter === "all"}
          onClick={() => setPharmacyDeliveryFilter("all")}
        >
          All
        </FilterButton>
        <FilterButton
          $active={pharmacyDeliveryFilter === "shipment_created"}
          onClick={() => setPharmacyDeliveryFilter("shipment_created")}
        >
          Shipment Created
        </FilterButton>
        <FilterButton
          $active={pharmacyDeliveryFilter === "shipment_delivered"}
          onClick={() => setPharmacyDeliveryFilter("shipment_delivered")}
        >
          Shipment Delivered
        </FilterButton>
        {hasActiveFilters && (
          <ClearFiltersButton onClick={clearDateFilters}>
            <FaTimes />
            Clear Filters
          </ClearFiltersButton>
        )}
      </DateFilterContainer>

      <OrdersContainer>
        <TopControls>
          <OrdersTitle>Order Requests Management</OrdersTitle>

          <SortContainer>
            <SortLabel>Sort by:</SortLabel>
            <SortSelect value={sortField} onChange={handleSortFieldChange}>
              <option value="order_date">Date</option>
              <option value="order_id">Order ID</option>
              <option value="patient_name">Patient Name</option>
              <option value="patient_dob">Patient DOB</option>
              <option value="total_fee">Total Fee</option>
              <option value="status">Status</option>
            </SortSelect>
            <SortIconButton
              onClick={toggleSortOrder}
              title={`Sort ${sortOrder === "asc" ? "Descending" : "Ascending"}`}
            >
              {sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />}
            </SortIconButton>
          </SortContainer>

          <CreateOrderBtn to="/orders/create">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 4V12M4 8H12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Create New Order
          </CreateOrderBtn>
        </TopControls>

        <Card>
          {loading ? (
            <LoadingMessage>
              <FaSpinner /> Loading orders...
            </LoadingMessage>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : orders.length === 0 ? (
            <NoOrdersMessage>
              {searchTerm
                ? `No orders found matching "${searchTerm}"`
                : "No orders found"}
            </NoOrdersMessage>
          ) : (
            <div style={{ overflowX: 'auto', borderRadius: '8px' }}>
              <OrdersTable>
                <thead>
                  <tr>
                    <SortableHeader
                      onClick={() => handleHeaderClick("order_id")}
                    >
                      ID
                      <span className="sort-icon">
                        {renderSortIcon("order_id")}
                      </span>
                    </SortableHeader>
                    <SortableHeader
                      onClick={() => handleHeaderClick("order_date")}
                    >
                      Date
                      <span className="sort-icon">
                        {renderSortIcon("order_date")}
                      </span>
                    </SortableHeader>
                    <SortableHeader
                      onClick={() => handleHeaderClick("patient_name")}
                    >
                      Name
                      <span className="sort-icon">
                        {renderSortIcon("patient_name")}
                      </span>
                    </SortableHeader>
                    <SortableHeader
                      onClick={() => handleHeaderClick("patient_dob")}
                    >
                      DOB
                      <span className="sort-icon">
                        {renderSortIcon("patient_dob")}
                      </span>
                    </SortableHeader>
                    <th>Shipping Address</th>
                    <th>Type</th>
                    <th>Pharmacy</th>
                    <th>Medicine(s)</th>
                    <SortableHeader
                      onClick={() => handleHeaderClick("total_fee")}
                    >
                      Total Fee
                      <span className="sort-icon">
                        {renderSortIcon("total_fee")}
                      </span>
                    </SortableHeader>
                    <SortableHeader onClick={() => handleHeaderClick("status")}>
                      Status
                      <span className="sort-icon">
                        {renderSortIcon("status")}
                      </span>
                    </SortableHeader>
                    <th>Delivery</th>
                    <th>Receipt</th>
                    <th>Actions</th>
                    {user?.is_superuser && <th>Admin Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order) => (
                    <React.Fragment key={order.order_id}>
                      <tr>
                        <td>{order.order_id}</td>
                        <td>
                          <DateContainer>
                            {formatDateCompact(
                              order.order_date,
                              order.order_time
                            )}
                          </DateContainer>
                        </td>
                        <td>
                          <PatientNameLink to={`/orders/${order.order_id}`}>
                            {order.patient_name ? order.patient_name : "N/A"}
                          </PatientNameLink>
                        </td>
                        <td>{formatPatientDobUtil(order.patient_dob)}</td>
                        <td>
                          {order.shipping_address ? (
                            <AddressContainer>
                              <div className="street">
                                {order.shipping_address.address1}
                                {order.shipping_address.address2 && (
                                  <>, {order.shipping_address.address2}</>
                                )}
                              </div>
                              <div className="city-state">
                                {order.shipping_address.city},{" "}
                                {order.shipping_address.state}{" "}
                                {order.shipping_address.zipcode}
                              </div>
                            </AddressContainer>
                          ) : (
                            <Badge variant="warning">N/A</Badge>
                          )}
                        </td>

                        <td style={{ width: "120px" }}>
                          {order?.order_type ? (
                            <Badge variant="info">{order.order_type}</Badge>
                          ) : (
                            <Badge variant="warning">N/A</Badge>
                          )}
                        </td>
                        <td style={{ width: "140px" }}>
                          {order.pharmacy?.pharmacy_name || <Badge variant="warning">N/A</Badge>}
                        </td>
                        <td style={{ width: "130px" }}>
                          {order.order_items?.length > 0 ? (
                            <ExpandButton
                              onClick={() => toggleOrderItems(order.order_id)}
                              expanded={expandedOrders[order.order_id]}
                            >
                              {order.order_items.length}{" "}
                              {order.order_items.length === 1
                                ? "medicine"
                                : "medicines"}
                              <FaChevronDown />
                            </ExpandButton>
                          ) : (
                            "No medicines"
                          )}
                        </td>
                        <td>{formatCurrency(order.total_fee)}</td>
                        <td style={{ width: "150px" }}>
                          <OrderStatus status={order.status}>
                            {getStatusDisplayText(order.status)}
                          </OrderStatus>
                        </td>
                        <td style={{ width: "160px" }}>
                          {(() => {
                            const items = order.order_items || [];
                            const created = items.filter(i => i.pharmacy_delivery_status === "shipment_created").length;
                            const delivered = items.filter(i => i.pharmacy_delivery_status === "shipment_delivered").length;
                            const hasAnyStatus = created > 0 || delivered > 0;
                            if (items.length === 0 || !hasAnyStatus) return <Badge variant="warning">N/A</Badge>;
                            return (
                              <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                                {created > 0 && (
                                  <PharmacyDeliveryStatus status="shipment_created">
                                    <FaBoxOpen /> {created} Shipped
                                  </PharmacyDeliveryStatus>
                                )}
                                {delivered > 0 && (
                                  <PharmacyDeliveryStatus status="shipment_delivered">
                                    <FaTruck /> {delivered} Delivered
                                  </PharmacyDeliveryStatus>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          <ReceiptActions>
                            <ReceiptButton
                              title="View Receipt"
                              onClick={() => handleViewReceipt(order.order_id)}
                            >
                              <FaReceipt />
                            </ReceiptButton>
                            <CopyButton
                              title="Copy Receipt URL"
                              onClick={(e) =>
                                handleCopyReceiptUrl(order.unique_id, e)
                              }
                            >
                              <FaCopy />
                            </CopyButton>
                          </ReceiptActions>
                        </td>
                        <td>
                          <ActionLinks>
                            <IconButton
                              title="View Details"
                              onClick={() =>
                                handleViewOrderDetails(order.order_id)
                              }
                            >
                              <FaEye />
                            </IconButton>

                            <IconButton
                              title={
                                order.status === "completed"
                                  ? "Cannot edit completed orders"
                                  : "Edit Order"
                              }
                              onClick={() => handleOpenEditModal(order)}
                              disabled={order.status === "completed"}
                              style={{
                                opacity:
                                  order.status === "completed" ||
                                  order.status === "Partially_completed"
                                    ? 0.5
                                    : 1,
                                cursor:
                                  order.status === "completed" ||
                                  order.status === "Partially_completed"
                                    ? "not-allowed"
                                    : "pointer"
                              }}
                            >
                              <FaEdit />
                            </IconButton>
                          </ActionLinks>
                        </td>
                        {user?.is_superuser && (
                          <td>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <DeleteButton
                                onClick={() => openDeleteModal(order)}
                                title="Delete Order"
                              >
                                <FaTrash />
                              </DeleteButton>
                              {order.status !== "completed" && order.status !== "in_progress" && (
                                <ApproveButton
                                  onClick={() => openApprovalModal(order)}
                                  disabled={approvalLoading}
                                  title="Approve Order"
                                >
                                  {approvalLoading ? (
                                    <FaSpinner className="fa-spin" />
                                  ) : (
                                    <FaCheck />
                                  )}
                                </ApproveButton>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                      {expandedOrders[order.order_id] &&
                        order.order_items?.length > 0 && (
                          <tr>
                            <td colSpan={user?.is_superuser ? 14 : 13}>
                              <OrderItemsContainer>
                                <OrderItemsTable>
                                  <thead>
                                    <tr>
                                      <th>Medicine</th>
                                      <th style={{ width: "150px" }}>
                                        Variant
                                      </th>
                                      <th>Notes</th>
                                      <th>Dosage</th>
                                      <th>Price</th>
                                      <th style={{ width: "150px" }}>Delivery Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {order.order_items.map((item, index) => (
                                      <tr key={index}>
                                        <td>
                                          {item.medicine?.medicine_name ||
                                            "N/A"}
                                        </td>
                                        <td style={{ width: "150px" }}>
                                          {item.variant ? (
                                            <Badge variant="info">
                                              {item.variant.quantity || ""} {item.variant.unit || ""}
                                            </Badge>
                                          ) : (
                                            <Badge variant="warning">N/A</Badge>
                                          )}
                                        </td>
                                        <td style={{ width: "150px" }}>
                                          {order.notes ? (
                                            <Badge variant="success">{order.notes}</Badge>
                                          ) : (
                                            <Badge variant="warning">N/A</Badge>
                                          )}
                                        </td>
                                                                <td>
                          {item.dosage_instructions[0]?.instruction ? (
                            <span>
                              {item.dosage_instructions[0].instruction}
                            </span>
                          ) : (
                            <Badge variant="warning">N/A</Badge>
                          )}
                        </td>
                                        <td>
                                          {formatCurrency(item.price || 0)}
                                        </td>
                                        <td style={{ width: "150px" }}>
                                          {item.pharmacy_delivery_status ? (
                                            <PharmacyDeliveryStatus status={item.pharmacy_delivery_status}>
                                              {item.pharmacy_delivery_status === "shipment_created" && <FaBoxOpen />}
                                              {item.pharmacy_delivery_status === "shipment_delivered" && <FaTruck />}
                                              {getPharmacyDeliveryStatusText(item.pharmacy_delivery_status)}
                                            </PharmacyDeliveryStatus>
                                          ) : (
                                            <Badge variant="warning">N/A</Badge>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </OrderItemsTable>
                              </OrderItemsContainer>
                            </td>
                          </tr>
                        )}
                    </React.Fragment>
                  ))}
                </tbody>
              </OrdersTable>

              <PaginationContainer>
                <PageInfo>
                  Showing {startIndex} to {endIndex} of {pagination.count}{" "}
                  entries
                </PageInfo>

                <div
                  style={{ display: "flex", gap: "16px", alignItems: "center" }}
                >
                  <PerPageContainer>
                    <PerPageLabel>Show</PerPageLabel>
                    <PerPageSelect
                      value={itemsPerPage}
                      onChange={handleItemsPerPageChange}
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={75}>75</option>
                      <option value={100}>100</option>
                    </PerPageSelect>
                    <PerPageLabel>entries</PerPageLabel>
                  </PerPageContainer>

                  <PaginationControls>
                    <PageButton
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.current_page === 1}
                    >
                      First
                    </PageButton>
                    <PageButton
                      onClick={() =>
                        handlePageChange(pagination.current_page - 1)
                      }
                      disabled={!pagination.previous}
                    >
                      Previous
                    </PageButton>

                    {Array.from(
                      { length: Math.min(5, pagination.total_pages) },
                      (_, i) => {
                        let pageNum;
                        if (pagination.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.current_page <= 3) {
                          pageNum = i + 1;
                        } else if (
                          pagination.current_page >=
                          pagination.total_pages - 2
                        ) {
                          pageNum = pagination.total_pages - 4 + i;
                        } else {
                          pageNum = pagination.current_page - 2 + i;
                        }

                        return (
                          <PageButton
                            key={pageNum}
                            $active={pageNum === pagination.current_page}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </PageButton>
                        );
                      }
                    )}

                    <PageInputContainer>
                      <PageInputLabel>Go to:</PageInputLabel>
                      <PageInput
                        type="text"
                        value={pageInputValue}
                        onChange={handlePageInputChange}
                        onKeyDown={handlePageInputKeyDown}
                        aria-label="Go to page"
                      />
                      <GoButton onClick={handleGoToPage}>Go</GoButton>
                    </PageInputContainer>

                    <PageButton
                      onClick={() =>
                        handlePageChange(pagination.current_page + 1)
                      }
                      disabled={!pagination.next}
                    >
                      Next
                    </PageButton>
                    <PageButton
                      onClick={() => handlePageChange(pagination.total_pages)}
                      disabled={
                        pagination.current_page === pagination.total_pages
                      }
                    >
                      Last
                    </PageButton>
                  </PaginationControls>
                </div>
              </PaginationContainer>
            </div>
          )}
        </Card>

        {selectedOrder && (
          <EditOrderModal
            isOpen={showEditModal}
            onClose={handleCloseEditModal}
            orderData={selectedOrder}
            onOrderUpdated={handleOrderUpdated}
          />
        )}

        {showApprovalModal && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Confirm Order Approval</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p>
                  Are you sure you want to approve order #
                  {orderToApprove?.order_id}?
                </p>
                <p>This action cannot be undone.</p>
              </ModalBody>
              <ModalFooter>
                <CancelButton
                  onClick={closeApprovalModal}
                  disabled={approvalLoading}
                >
                  Cancel
                </CancelButton>
                <ApproveButton
                  onClick={handleCompleteOrder}
                  disabled={approvalLoading}
                >
                  {approvalLoading ? "Processing..." : "Yes, Approve Order"}
                </ApproveButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        {showDeleteModal && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Confirm Order Deletion</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p>
                  Are you sure you want to delete order #
                  {orderToDelete?.order_id}?
                </p>
                <p style={{ color: "var(--error)", fontWeight: "bold" }}>
                  This action cannot be undone.
                </p>
                {orderToDelete?.status === "completed" && (
                  <p style={{ color: "var(--warning)", marginTop: "10px" }}>
                    Warning: This order has been completed. Deleting it may
                    affect reporting and record-keeping.
                  </p>
                )}
              </ModalBody>
              <ModalFooter>
                <CancelButton
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                >
                  Cancel
                </CancelButton>
                <DangerButton
                  onClick={handleDeleteOrder}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <FaSpinner className="fa-spin" /> Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash /> Yes, Delete Order
                    </>
                  )}
                </DangerButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        {showResponseModal && pharmacyResponse && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Pharmacy Order Status</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <PharmacyResponseContainer>
                  <PharmacyResponseHeader>
                    <ResponseStatus success={pharmacyResponse.overall_success}>
                      {pharmacyResponse.overall_success ? (
                        <>
                          <FaCheckCircle />
                          Order has been placed successfully on{" "}
                          {pharmacyResponse.pharmacy_name}
                        </>
                      ) : (
                        <>
                          <FaTimesCircle />
                          Order placing error on{" "}
                          {pharmacyResponse.pharmacy_name}
                        </>
                      )}
                    </ResponseStatus>
                  </PharmacyResponseHeader>

                  <p>Total medications: {pharmacyResponse.medication_count}</p>

                  <MedicationList>
                    {pharmacyResponse.medications.map((med, index) => (
                      <MedicationItem key={index}>
                        <StatusIcon success={med.success}>
                          {med.success ? <FaCheckCircle /> : <FaTimesCircle />}
                        </StatusIcon>
                        <span>{med.medicine_name}</span>
                      </MedicationItem>
                    ))}
                  </MedicationList>

                  {pharmacyResponse.info && (
                    <div style={{ marginTop: "1rem" }}>
                      <p>
                        <strong>Additional Information:</strong>
                      </p>
                      <p>{pharmacyResponse.info}</p>
                    </div>
                  )}

                  {pharmacyResponse.errors &&
                    pharmacyResponse.errors.length > 0 && (
                      <div style={{ marginTop: "1rem" }}>
                        <p>
                          <strong>Errors:</strong>
                        </p>
                        <ErrorList>
                          {pharmacyResponse.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ErrorList>
                      </div>
                    )}
                </PharmacyResponseContainer>
              </ModalBody>
              <ModalFooter>
                <Button onClick={closeResponseModal}>Close</Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        {showErrorModal && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Order Approval Error</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p style={{ color: "var(--error)", marginBottom: "1rem" }}>
                  {errorMessage}
                </p>
                <p>
                  The order status has been updated. Please refresh the page to see the current status.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button onClick={closeErrorModal}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </OrdersContainer>

      {/* Toast notification for copy URL */}
      {copyNotification && (
        <CopyNotification>
          <FaCheckCircle />
          Receipt URL for Order #{copiedOrderId} copied to clipboard!
        </CopyNotification>
      )}
    </PageWrapper>
  );
};

export default OrdersPage;
